import { useCallback, useEffect, useState } from 'react';
import { localStorage } from 'dibs-browser-storage';

const RECENT_HISTORY_LIST = 'nft_recent_history_list';
const MAX_STORAGE_DAYS = 180; // 6 months
const MAX_RECENTLY_VIEWED_ITEMS_STORAGE = 20;

type LocalHistoryItemType = {
    itemId: string;
    timestamp: string;
};

const daysBetween = (timestamp1: number, timestamp2: number): number => {
    const timeDiff = Math.abs(timestamp1 - timestamp2);
    const oneDayInMs = 86400000; // 24h/d * 60m/h * 60s/m * 1000ms/s
    return Math.round(timeDiff / oneDayInMs);
};

const isValidRecentHistoryItem = (
    item: LocalHistoryItemType,
    currentItemId?: string | null
): boolean => {
    const currentTime = new Date().getTime();
    const isCurrentItem = item.itemId === (currentItemId || '');
    return (
        daysBetween(parseInt(item.timestamp, 10), currentTime) < MAX_STORAGE_DAYS && !isCurrentItem
    );
};

const getLocalRecentHistoryList = (
    currentItemId?: string | null,
    maxRecentHistoryItems: number = MAX_RECENTLY_VIEWED_ITEMS_STORAGE
): LocalHistoryItemType[] => {
    const localRecentHistoryList =
        (localStorage.getItem(RECENT_HISTORY_LIST) as LocalHistoryItemType[]) || [];
    const recentHistoryList: LocalHistoryItemType[] = [];
    const index = maxRecentHistoryItems * -1;

    if (Array.isArray(localRecentHistoryList)) {
        localRecentHistoryList.forEach((localRecentHistoryItem: LocalHistoryItemType) => {
            if (isValidRecentHistoryItem(localRecentHistoryItem, currentItemId)) {
                recentHistoryList.push(localRecentHistoryItem);
            }
        });
    }

    if (recentHistoryList.length > maxRecentHistoryItems) {
        return recentHistoryList.slice(index);
    }
    return recentHistoryList;
};

const addLocalRecentHistoryItem = ({ itemId }: { itemId: string }): void => {
    const currentDate = new Date();
    const currentItem: LocalHistoryItemType = {
        itemId,
        timestamp: currentDate.getTime().toString(),
    };

    const localRecentHistoryList = getLocalRecentHistoryList(currentItem.itemId) || [];

    localRecentHistoryList.push(currentItem);

    localStorage.setItem(RECENT_HISTORY_LIST, JSON.stringify(localRecentHistoryList));
};

export const recentItemHistory = (): string[] => {
    const localRecentItems = getLocalRecentHistoryList(undefined).reverse();
    return localRecentItems.map(({ itemId }) => itemId);
};

export const useRecentHistory = (): {
    itemHistory: string[];
    addItemToHistory: (itemId: string) => void;
} => {
    const [itemHistory, setItemHistory] = useState<string[] | []>([]);

    useEffect(() => {
        const localRecentItems = getLocalRecentHistoryList(undefined).reverse();
        setItemHistory(localRecentItems.map(({ itemId }) => itemId));
    }, []);

    const addItemToHistory = useCallback((itemId: string) => {
        addLocalRecentHistoryItem({ itemId });
    }, []);

    return { itemHistory, addItemToHistory };
};
